/**
 * Live preview of what a formula cell will hold once the accepted fills are
 * written — so a pricing schedule's Amount and TOTAL move as you work instead
 * of sitting blank until you download the file.
 *
 * THE RULE THAT MAKES THIS SAFE: we never guess. Only a small, unambiguous
 * subset is evaluated — `+ - * /`, parentheses, percentages and SUM/PRODUCT/
 * ROUND/MIN/MAX/AVERAGE over resolvable numeric cells. Excel and JavaScript use
 * the same IEEE-754 doubles, so for that subset the answer is identical, not
 * merely close. Anything outside it (a lookup, a function we do not implement,
 * a reference to another sheet, a cell holding text) yields NO NUMBER AT ALL,
 * and the caller shows "calculates in Excel". On a priced tender a blank with
 * an explanation is always better than a plausible wrong figure.
 *
 * The authoritative numbers still come from LibreOffice at export; this only
 * fills the gap between accepting a fill and downloading the file.
 */

import type { FillProposal, SheetModel, WorkbookModel } from '@/types/schedule'
import { cellTargetOf } from '@/lib/fill-source'

/** Functions we implement exactly. Everything else refuses. */
const SUPPORTED_FUNCTIONS = new Set(['SUM', 'PRODUCT', 'ROUND', 'MIN', 'MAX', 'AVERAGE'])

/** Guards against a cyclic or pathological workbook eating the main thread. */
const MAX_DEPTH = 16

type Cursor = { text: string; pos: number }

function parseAddress(address: string): { col: number; row: number } | null {
  const match = /^\$?([A-Z]{1,3})\$?(\d{1,7})$/.exec(address.toUpperCase())
  if (!match) return null
  let col = 0
  for (const ch of match[1]!) col = col * 26 + (ch.charCodeAt(0) - 64)
  return { col, row: Number(match[2]) }
}

function columnLetters(index: number): string {
  let n = index
  let out = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    out = String.fromCharCode(65 + rem) + out
    n = Math.floor((n - 1) / 26)
  }
  return out
}

/**
 * The value a cell will hold once the pending/accepted fills are applied, or
 * null when it cannot be resolved to a number.
 */
function resolveCell(
  sheet: SheetModel,
  address: string,
  overlay: Map<string, string>,
  depth: number
): number | null {
  if (depth > MAX_DEPTH) return null

  const staged = overlay.get(address)
  if (staged !== undefined) {
    const n = Number(staged.replace(/,/g, ''))
    return Number.isFinite(n) ? n : null
  }

  const cell = sheet.rows.flatMap((row) => row.cells).find((c) => c.address === address)
  if (!cell) return 0 // an untouched empty cell is zero to Excel's arithmetic

  // A formula feeding another formula (Amount → TOTAL) is resolved recursively;
  // without its text we cannot know the value, so the whole chain refuses.
  if (cell.isFormula) {
    if (!cell.formula) return null
    return evaluateFormula(cell.formula, sheet, overlay, depth + 1)
  }

  if (typeof cell.value === 'number') return cell.value
  if (cell.value === null || cell.value === '') return 0
  // Text in an arithmetic chain is exactly where a guess would go wrong.
  return null
}

function expandRange(from: string, to: string): string[] | null {
  const a = parseAddress(from)
  const b = parseAddress(to)
  if (!a || !b) return null
  const addresses: string[] = []
  for (let r = Math.min(a.row, b.row); r <= Math.max(a.row, b.row); r++) {
    for (let c = Math.min(a.col, b.col); c <= Math.max(a.col, b.col); c++) {
      addresses.push(`${columnLetters(c)}${r}`)
    }
  }
  return addresses
}

// ── Recursive-descent parser over the supported grammar ────────────────────

function skipSpace(cur: Cursor): void {
  while (cur.pos < cur.text.length && cur.text[cur.pos] === ' ') cur.pos++
}

function parseExpression(
  cur: Cursor,
  sheet: SheetModel,
  overlay: Map<string, string>,
  depth: number
): number | null {
  let left = parseTerm(cur, sheet, overlay, depth)
  if (left === null) return null

  for (;;) {
    skipSpace(cur)
    const op = cur.text[cur.pos]
    if (op !== '+' && op !== '-') return left
    cur.pos++
    const right = parseTerm(cur, sheet, overlay, depth)
    if (right === null) return null
    left = op === '+' ? left + right : left - right
  }
}

function parseTerm(
  cur: Cursor,
  sheet: SheetModel,
  overlay: Map<string, string>,
  depth: number
): number | null {
  let left = parseFactor(cur, sheet, overlay, depth)
  if (left === null) return null

  for (;;) {
    skipSpace(cur)
    const op = cur.text[cur.pos]
    if (op !== '*' && op !== '/') return left
    cur.pos++
    const right = parseFactor(cur, sheet, overlay, depth)
    if (right === null) return null
    // Excel shows #DIV/0! here; refusing keeps us out of the business of
    // rendering error tokens we did not compute authoritatively.
    if (op === '/' && right === 0) return null
    left = op === '*' ? left * right : left / right
  }
}

function parseFactor(
  cur: Cursor,
  sheet: SheetModel,
  overlay: Map<string, string>,
  depth: number
): number | null {
  skipSpace(cur)
  if (cur.pos >= cur.text.length) return null

  if (cur.text[cur.pos] === '-') {
    cur.pos++
    const value = parseFactor(cur, sheet, overlay, depth)
    return value === null ? null : -value
  }

  if (cur.text[cur.pos] === '(') {
    cur.pos++
    const value = parseExpression(cur, sheet, overlay, depth)
    skipSpace(cur)
    if (value === null || cur.text[cur.pos] !== ')') return null
    cur.pos++
    return applyPercent(cur, value)
  }

  // Function call
  const fn = /^([A-Z]+)\(/.exec(cur.text.slice(cur.pos).toUpperCase())
  if (fn) {
    const name = fn[1]!
    if (!SUPPORTED_FUNCTIONS.has(name)) return null
    cur.pos += fn[0].length
    const args = parseArguments(cur, sheet, overlay, depth)
    if (args === null) return null
    const result = applyFunction(name, args)
    return result === null ? null : applyPercent(cur, result)
  }

  // Range or single reference
  const ref = /^(\$?[A-Z]{1,3}\$?\d{1,7})(?::(\$?[A-Z]{1,3}\$?\d{1,7}))?/.exec(
    cur.text.slice(cur.pos).toUpperCase()
  )
  if (ref) {
    cur.pos += ref[0].length
    if (ref[2]) return null // a bare range is only valid inside a function
    const value = resolveCell(sheet, ref[1]!.replace(/\$/g, ''), overlay, depth)
    return value === null ? null : applyPercent(cur, value)
  }

  // Literal number
  const num = /^\d+(\.\d+)?/.exec(cur.text.slice(cur.pos))
  if (num) {
    cur.pos += num[0].length
    return applyPercent(cur, Number(num[0]))
  }

  return null
}

function applyPercent(cur: Cursor, value: number): number {
  if (cur.text[cur.pos] === '%') {
    cur.pos++
    return value / 100
  }
  return value
}

/** Flattens arguments, expanding `A1:A9` ranges into their cells. */
function parseArguments(
  cur: Cursor,
  sheet: SheetModel,
  overlay: Map<string, string>,
  depth: number
): number[] | null {
  const values: number[] = []

  for (;;) {
    skipSpace(cur)
    if (cur.text[cur.pos] === ')') {
      cur.pos++
      return values
    }

    const rangeMatch = /^(\$?[A-Z]{1,3}\$?\d{1,7}):(\$?[A-Z]{1,3}\$?\d{1,7})/.exec(
      cur.text.slice(cur.pos).toUpperCase()
    )
    if (rangeMatch) {
      cur.pos += rangeMatch[0].length
      const addresses = expandRange(
        rangeMatch[1]!.replace(/\$/g, ''),
        rangeMatch[2]!.replace(/\$/g, '')
      )
      if (!addresses) return null
      for (const address of addresses) {
        const value = resolveCell(sheet, address, overlay, depth)
        if (value === null) return null
        values.push(value)
      }
    } else {
      const value = parseExpression(cur, sheet, overlay, depth)
      if (value === null) return null
      values.push(value)
    }

    skipSpace(cur)
    if (cur.text[cur.pos] === ',') {
      cur.pos++
      continue
    }
    if (cur.text[cur.pos] === ')') {
      cur.pos++
      return values
    }
    return null
  }
}

function applyFunction(name: string, args: number[]): number | null {
  switch (name) {
    case 'SUM':
      return args.reduce((total, n) => total + n, 0)
    case 'PRODUCT':
      return args.reduce((total, n) => total * n, 1)
    case 'MIN':
      return args.length > 0 ? Math.min(...args) : null
    case 'MAX':
      return args.length > 0 ? Math.max(...args) : null
    case 'AVERAGE':
      return args.length > 0 ? args.reduce((t, n) => t + n, 0) / args.length : null
    case 'ROUND': {
      const [value, digits] = args
      if (value === undefined) return null
      const factor = 10 ** Math.trunc(digits ?? 0)
      return Math.round(value * factor) / factor
    }
    default:
      return null
  }
}

/** Evaluates one formula, or null when any part of it falls outside the subset. */
export function evaluateFormula(
  formula: string,
  sheet: SheetModel,
  overlay: Map<string, string>,
  depth = 0
): number | null {
  // Cross-sheet and workbook references are out of scope: resolving them means
  // reasoning about a sheet we were not handed.
  if (formula.includes('!') || formula.includes('[')) return null

  const cur: Cursor = { text: formula.trim().replace(/^=/, ''), pos: 0 }
  const value = parseExpression(cur, sheet, overlay, depth)
  skipSpace(cur)
  // Trailing junk means we mis-parsed; refuse rather than return a partial answer.
  if (cur.pos !== cur.text.length) return null
  return value !== null && Number.isFinite(value) ? value : null
}

/**
 * The values that WILL be written, keyed by address, for one sheet.
 *
 * Mirrors the export's own selection exactly — accepted fills, the user's edit
 * winning over the proposal, blanks excluded — so the preview can never be
 * computed from a different input set than the file ends up with.
 */
export function buildOverlay(proposals: FillProposal[], sheetName: string): Map<string, string> {
  const overlay = new Map<string, string>()
  for (const proposal of proposals) {
    const cell = cellTargetOf(proposal)
    if (!cell || cell.sheet !== sheetName) continue
    if (proposal.status !== 'accepted') continue
    const value = (proposal.userEditedValue ?? proposal.proposedValue).trim()
    if (value.length === 0) continue
    overlay.set(cell.address, value)
  }
  return overlay
}

/**
 * Previewed values for every formula cell on a sheet that we can evaluate
 * honestly. Cells outside the supported subset are simply absent.
 */
export function previewFormulaValues(
  model: WorkbookModel,
  sheetName: string,
  proposals: FillProposal[]
): Map<string, number> {
  const sheet = model.sheets.find((s) => s.name === sheetName)
  if (!sheet) return new Map()

  const overlay = buildOverlay(proposals, sheetName)
  const previews = new Map<string, number>()

  for (const row of sheet.rows) {
    for (const cell of row.cells) {
      if (!cell.isFormula || !cell.formula) continue
      const value = evaluateFormula(cell.formula, sheet, overlay)
      if (value !== null) previews.set(cell.address, value)
    }
  }

  return previews
}
